import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

// Reports the logged-in user's current location so the admin "Live now" board shows who's
// actually here RIGHT NOW. "Live" must mean present-and-active — NOT "has a tab open".
//
// We only ping when BOTH are true:
//   - the tab is visible (document.visibilityState === 'visible')
//   - the user interacted within the last few minutes (not idle)
// When either stops being true the pings stop, and the backend's short Redis TTL drops the
// session within ~a ping cycle. On top of that we fire a one-shot "leave" beacon the instant
// the tab is hidden or the page is closed, so switching away clears almost immediately.
//
// This fixes the false-positive where a helpdesk left open in a background tab (agent went
// home, laptop still on) kept pinging on the old unconditional 25s timer and showed as live.
const IDLE_MS = 3 * 60 * 1000; // no interaction in 3 min → treat as away, stop pinging
const PING_MS = 20000;         // comfortably under the backend 50s TTL

export default function LiveActivityPinger() {
  const { user } = useAuth();
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const pathRef = useRef('');
  pathRef.current = (location.pathname || '') + (location.search || '');

  useEffect(() => {
    if (!user) return undefined;

    const markActive = () => { lastActivityRef.current = Date.now(); };
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    activityEvents.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const isLive = () =>
      document.visibilityState === 'visible' &&
      (Date.now() - lastActivityRef.current) < IDLE_MS;

    const ping = () => {
      if (!isLive()) return;
      api.post('/live/ping', { path: pathRef.current, title: document.title }).catch(() => {});
    };

    // Drop the session immediately when the tab is hidden or the page is being unloaded,
    // rather than waiting out the TTL. sendBeacon can't set an auth header, so token in body.
    const leave = () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token && navigator.sendBeacon) {
          navigator.sendBeacon(
            `${api.defaults.baseURL}/live/leave`,
            new Blob([JSON.stringify({ token })], { type: 'application/json' })
          );
        }
      } catch { /* ignore */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') { markActive(); ping(); }
      else leave();
    };

    ping();
    const t = setInterval(ping, PING_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', leave);

    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', leave);
      activityEvents.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, [user]);

  // Reflect navigation promptly: ping the new route as soon as it changes (gated the same way
  // inside ping()). Kept separate so the timer/listeners above are set up only once per login.
  useEffect(() => {
    if (!user) return;
    if (document.visibilityState !== 'visible') return;
    api.post('/live/ping', {
      path: (location.pathname || '') + (location.search || ''),
      title: document.title,
    }).catch(() => {});
  }, [user, location.pathname, location.search]);

  return null;
}
